/**
 * Dot-lattice geometry for the San Francisco Bay Area.
 *
 * Land is everything inside the frame that is not ocean and not bay, faded out
 * away from the bay so the frame has no hard rectangular edge.
 */

import type { MapGeometry, MapHub, Point, Route } from "./us-map";

/** Pacific coastline north to south, closed off to the far west. */
const PACIFIC_FLAT = [
  -123.6, 38.35, -123.1, 38.35, -123.05, 38.3, -122.99, 38.24, -122.96, 38.16,
  -122.98, 38.1, -122.92, 38.03, -123.0, 38.0, -123.02, 37.99, -122.96, 37.94,
  -122.83, 37.92, -122.7, 37.9, -122.6, 37.86, -122.52, 37.81, -122.51, 37.75,
  -122.51, 37.69, -122.5, 37.6, -122.45, 37.52, -122.44, 37.46, -122.4, 37.38,
  -122.34, 37.28, -122.28, 37.2, -122.2, 37.1, -122.1, 37.01, -122.02, 36.95,
  -121.93, 36.93, -121.85, 36.9, -123.6, 36.9,
];

/**
 * San Francisco Bay, San Pablo Bay, Carquinez Strait and Suisun Bay as one
 * ring, clockwise from the Golden Gate.
 */
const BAY_FLAT = [
  -122.478, 37.828, -122.44, 37.808, -122.393, 37.795, -122.378, 37.74, -122.39,
  37.7, -122.385, 37.65, -122.34, 37.59, -122.29, 37.545, -122.23, 37.505,
  -122.155, 37.47, -122.07, 37.45, -122.0, 37.445, -121.96, 37.463, -121.985,
  37.495, -122.03, 37.53, -122.08, 37.58, -122.125, 37.64, -122.18, 37.7,
  -122.245, 37.765, -122.3, 37.83, -122.34, 37.895, -122.375, 37.945, -122.4,
  37.99, -122.37, 38.045, -122.3, 38.09, -122.23, 38.115, -122.15, 38.1, -122.1,
  38.065, -122.03, 38.06, -121.96, 38.06, -121.9, 38.05, -121.905, 38.02,
  -121.98, 38.028, -122.06, 38.03, -122.11, 38.04, -122.18, 38.075, -122.27,
  38.09, -122.35, 38.1, -122.43, 38.08, -122.47, 38.03, -122.48, 37.985,
  -122.46, 37.945, -122.485, 37.905, -122.47, 37.87, -122.478, 37.845,
];

function pairs(flat: number[]): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < flat.length; i += 2) {
    out.push([flat[i] as number, flat[i + 1] as number]);
  }
  return out;
}

const PACIFIC = pairs(PACIFIC_FLAT);
const BAY = pairs(BAY_FLAT);

interface BayHub {
  readonly name: string;
  readonly lon: number;
  readonly lat: number;
  readonly home?: boolean;
}

/** Bay Area research and lab locations. The venue is the point they converge on. */
export const BAY_HUBS: readonly BayHub[] = [
  { name: "San Francisco", lon: -122.4194, lat: 37.7749, home: true },
  { name: "Berkeley", lon: -122.2585, lat: 37.8719 },
  { name: "Oakland", lon: -122.2712, lat: 37.8044 },
  { name: "Emeryville", lon: -122.2853, lat: 37.8313 },
  { name: "Sausalito", lon: -122.4853, lat: 37.8591 },
  { name: "South San Francisco", lon: -122.4077, lat: 37.6547 },
  { name: "San Mateo", lon: -122.3255, lat: 37.563 },
  { name: "Redwood City", lon: -122.2364, lat: 37.4852 },
  { name: "Menlo Park", lon: -122.1817, lat: 37.453 },
  { name: "Palo Alto", lon: -122.1697, lat: 37.4275 },
  { name: "Mountain View", lon: -122.0839, lat: 37.3861 },
  { name: "Sunnyvale", lon: -122.0363, lat: 37.3688 },
  { name: "Santa Clara", lon: -121.9552, lat: 37.3541 },
  { name: "San Jose", lon: -121.8863, lat: 37.3382 },
  { name: "Fremont", lon: -121.9886, lat: 37.5485 },
];

const LON_0 = -123.06;
const LON_1 = -121.72;
const LAT_0 = 37.3;
const LAT_1 = 38.12;
/** The dissolve is centred on the bay, not on the frame. */
const FOCUS_LON = -122.26;
const FOCUS_LAT = 37.76;
/** Longitude compression at this latitude; accurate to well under a pixel here. */
const KX = Math.cos((37.72 * Math.PI) / 180);

function pointInPolygon(x: number, y: number, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i] as Point;
    const [xj, yj] = poly[j] as Point;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export function buildBayGeometry(width: number, pitch: number): MapGeometry {
  const scale = width / ((LON_1 - LON_0) * KX);
  const height = (LAT_1 - LAT_0) * scale;

  const project = (lon: number, lat: number): Point => [
    (lon - LON_0) * KX * scale,
    (LAT_1 - lat) * scale,
  ];

  const pacific = PACIFIC.map(([lon, lat]) => project(lon, lat));
  const bay = BAY.map(([lon, lat]) => project(lon, lat));
  const [focusX, focusY] = project(FOCUS_LON, FOCUS_LAT);

  const dots: number[] = [];
  const alphas: number[] = [];
  const rows = Math.floor(height / pitch) + 2;
  const cols = Math.floor(width / pitch) + 2;
  for (let iy = 0; iy < rows; iy++) {
    for (let ix = 0; ix < cols; ix++) {
      const x = ix * pitch + (iy % 2 ? pitch / 2 : 0);
      const y = iy * pitch;
      if (x > width + pitch) {
        continue;
      }
      if (pointInPolygon(x, y, pacific) || pointInPolygon(x, y, bay)) {
        continue;
      }
      const d = Math.hypot(
        (x - focusX) / (width * 0.54),
        (y - focusY) / (height * 0.8),
      );
      const alpha = Math.max(0, Math.min(1, 1.42 - 1.25 * d));
      if (alpha <= 0.03) {
        continue;
      }
      dots.push(x, y);
      alphas.push(alpha);
    }
  }

  const hubs: MapHub[] = BAY_HUBS.map((hub) => {
    const [hx, hy] = project(hub.lon, hub.lat);
    let bestX = hx;
    let bestY = hy;
    let bestD = Infinity;
    for (let i = 0; i < dots.length; i += 2) {
      const dx = (dots[i] as number) - hx;
      const dy = (dots[i + 1] as number) - hy;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        bestX = dots[i] as number;
        bestY = dots[i + 1] as number;
      }
    }
    return { name: hub.name, x: bestX, y: bestY, home: hub.home === true };
  });

  const home = hubs.find((h) => h.home);
  const taken = new Set(hubs.map((h) => `${h.x},${h.y}`));
  const base: number[] = [];
  const baseAlpha: number[] = [];
  for (let i = 0; i < dots.length; i += 2) {
    if (!taken.has(`${dots[i]},${dots[i + 1]}`)) {
      base.push(dots[i] as number, dots[i + 1] as number);
      baseAlpha.push(alphas[i / 2] as number);
    }
  }

  const routes: Route[] = home
    ? hubs
        .filter((h) => !h.home)
        .map((h) => {
          const dx = home.x - h.x;
          const dy = home.y - h.y;
          const len = Math.hypot(dx, dy) || 1;
          const k = 0.17 * len;
          return {
            x0: h.x,
            y0: h.y,
            cx: (h.x + home.x) / 2 + (-dy / len) * k,
            cy: (h.y + home.y) / 2 + (dx / len) * k,
            x1: home.x,
            y1: home.y,
          };
        })
    : [];

  return {
    width,
    height,
    pitch,
    radius: pitch * 0.215,
    base: Float32Array.from(base),
    alpha: Float32Array.from(baseAlpha),
    hubs,
    routes,
  };
}
